import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "../components/ConfirmModal";

/**
 * Waarschuwt bij het verlaten van een scherm met niet-opgeslagen wijzigingen.
 * De app gebruikt een gewone BrowserRouter (geen data router, dus geen useBlocker); daarom worden de
 * verlaatwegen apart onderschept:
 *  - sluiten of herladen van het tabblad (beforeunload)
 *  - klikken op een interne link (menu, breadcrumbs, ...), onderschept in de capture-fase
 *  - de terugknop van de browser (popstate, met een extra historie-item als stootkussen)
 *  - `guardedNavigate(path)` voor knoppen in het scherm zelf
 *
 * Gebruik: `const { prompt, guardedNavigate } = useUnsavedChangesGuard(dirty);` en render `{prompt}`.
 */
export function useUnsavedChangesGuard(dirty) {
  const navigate = useNavigate();
  const [pending, setPending] = useState(null); // { type: "path", path } | { type: "back" }
  const leaving = useRef(false);
  const sentinel = useRef(false);

  // tabblad sluiten of herladen
  useEffect(() => {
    if (!dirty) return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty]);

  // interne links
  useEffect(() => {
    if (!dirty) return undefined;
    const onClick = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = e.target.closest && e.target.closest("a[href]");
      if (!anchor || (anchor.target && anchor.target !== "_self") || anchor.hasAttribute("download")) return;
      const url = new URL(anchor.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      const path = url.pathname + url.search + url.hash;
      if (path === window.location.pathname + window.location.search + window.location.hash) return;
      e.preventDefault();
      e.stopPropagation();
      setPending({ type: "path", path });
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [dirty]);

  // terugknop: een extra item op de historie vangt de eerste "terug" op
  useEffect(() => {
    if (!dirty) return undefined;
    window.history.pushState({ unsavedGuard: true }, "", window.location.href);
    sentinel.current = true;
    const onPopState = () => {
      if (leaving.current) return;
      sentinel.current = false;
      setPending({ type: "back" });
    };
    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
      // opgeslagen of ongedaan gemaakt zonder weg te gaan: het stootkussen weer weghalen
      if (sentinel.current && !leaving.current && window.history.state?.unsavedGuard) {
        leaving.current = true;
        window.history.back();
        setTimeout(() => { leaving.current = false; }, 0);
      }
      sentinel.current = false;
    };
  }, [dirty]);

  const stay = useCallback(() => {
    if (pending?.type === "back" && dirty && !sentinel.current) {
      window.history.pushState({ unsavedGuard: true }, "", window.location.href);
      sentinel.current = true;
    }
    setPending(null);
  }, [pending, dirty]);

  const leave = useCallback(() => {
    const target = pending;
    leaving.current = true;
    sentinel.current = false;
    setPending(null);
    if (target?.type === "path") navigate(target.path);
    else window.history.back();
  }, [pending, navigate]);

  const guardedNavigate = useCallback(
    (path) => {
      if (dirty) setPending({ type: "path", path });
      else navigate(path);
    },
    [dirty, navigate]
  );

  const prompt = pending ? (
    <ConfirmModal
      title="Niet-opgeslagen wijzigingen"
      message="Je hebt wijzigingen die nog niet opgeslagen zijn. Als je deze pagina verlaat, gaan ze verloren."
      confirmLabel="Pagina verlaten"
      cancelLabel="Blijven"
      danger
      onConfirm={leave}
      onCancel={stay}
    />
  ) : null;

  return { prompt, guardedNavigate };
}
